import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Route, RefreshCw, ChevronDown, ChevronRight, Copy, ClipboardList, X,
  Check, Ban, Clock, AlertCircle, MessageCircle, Phone, Mail, MapPin, Users, Trophy,
} from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const STATUS_META = {
  pending:           { label: "Pending",           icon: Clock,       cls: "bg-slate-100 text-slate-600 border-slate-200" },
  done:              { label: "Done",              icon: Check,       cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  no_response:       { label: "No Response",       icon: AlertCircle, cls: "bg-amber-100 text-amber-700 border-amber-200" },
  skipped:           { label: "Skipped",           icon: Ban,         cls: "bg-slate-200 text-slate-600 border-slate-300" },
  rescheduled:       { label: "Rescheduled",       icon: Clock,       cls: "bg-blue-100 text-blue-700 border-blue-200" },
  negative_response: { label: "Negative Response", icon: X,           cls: "bg-red-100 text-red-700 border-red-200" },
};

const CHANNEL_ICON = {
  whatsapp:   MessageCircle,
  phone_call: Phone,
  email:      Mail,
  site_visit: MapPin,
  in_person:  Users,
};

export const NurtureJourneyCard = ({ apiBase, leadId, onDismiss }) => {
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [expandedTp, setExpandedTp] = useState(null);
  const [logOpenTp, setLogOpenTp] = useState(null);
  const [logStatus, setLogStatus] = useState("done");
  const [logNotes, setLogNotes] = useState("");
  const [logSubmitting, setLogSubmitting] = useState(false);
  const [promotingTp, setPromotingTp] = useState(null);

  const PROMOTABLE_CHANNELS = new Set(["whatsapp", "email", "phone_call"]);

  const loadActive = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/ai/nurture-journey/${leadId}/active`);
      setJourney(res.data || null);
    } catch (e) {
      console.error("load active nurture failed", e);
    }
  }, [apiBase, leadId]);

  useEffect(() => { loadActive(); }, [loadActive]);

  const createJourney = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${apiBase}/ai/nurture-journey/${leadId}`);
      setJourney(res.data);
      toast.success("Nurture journey created");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create journey");
    } finally { setLoading(false); }
  };

  const regenerate = async () => {
    if (!journey?.journey_id) return;
    setRegenerating(true);
    try {
      const res = await axios.post(`${apiBase}/ai/nurture-journey/${journey.journey_id}/regenerate`);
      setJourney(res.data);
      toast.success("Journey regenerated from feedback");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to regenerate");
    } finally { setRegenerating(false); }
  };

  const openLog = (tp) => {
    setLogOpenTp(tp);
    setLogStatus("done");
    setLogNotes("");
  };

  const submitLog = async () => {
    if (!logOpenTp || !journey?.journey_id) return;
    setLogSubmitting(true);
    try {
      const res = await axios.post(
        `${apiBase}/ai/nurture-journey/${journey.journey_id}/touchpoint/${logOpenTp.id}/feedback`,
        { status: logStatus, notes: logNotes }
      );
      toast.success("Feedback logged" + (res.data?.auto_regenerated ? " — journey auto-adapted" : ""));
      setLogOpenTp(null);
      await loadActive();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to log feedback");
    } finally { setLogSubmitting(false); }
  };

  const copyScript = (txt) => {
    navigator.clipboard.writeText(txt || "");
    toast.success("Script copied");
  };

  const promoteToTemplate = async (tp) => {
    if (!journey?.journey_id) return;
    setPromotingTp(tp.id);
    try {
      const res = await axios.post(
        `${apiBase}/ai/nurture-journey/${journey.journey_id}/touchpoint/${tp.id}/promote-template`
      );
      if (res.data?.created) {
        toast.success(`Saved to template registry — "${res.data.template_name}" pending approval`);
      } else {
        toast.success(`Win logged against existing template — ${res.data.wins_added} more win(s) rolled up`);
      }
      await loadActive();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to promote template");
    } finally { setPromotingTp(null); }
  };

  if (!journey) {
    return (
      <Card className="bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-500 rounded-xl flex items-center justify-center">
              <Route className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-semibold text-violet-800">Nurture Journey</p>
              <p className="text-xs text-violet-600">Generate a personalized plan for this buyer</p>
            </div>
          </div>
          <Button onClick={createJourney} disabled={loading}
                  className="bg-violet-600 text-white hover:bg-violet-700"
                  data-testid="nurture-create-btn">
            {loading ? "Generating…" : "Create Nurture Plan"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const touchpoints = journey.touchpoints || [];

  return (
    <>
      <Card className="bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200" data-testid="nurture-journey-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-500 rounded-xl flex items-center justify-center">
                <Route className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-violet-800">
                  {journey.journey_name}
                  {journey.regenerated_count > 0 && (
                    <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-violet-200 text-violet-700">
                      adapted v{journey.regenerated_count + 1}
                    </span>
                  )}
                  {journey.seeded_scripts_count > 0 && (
                    <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 inline-flex items-center gap-1"
                          data-testid="nurture-seeded-badge"
                          title={`This journey was seeded with ${journey.seeded_scripts_count} proven script(s) that already won ${journey.seeded_total_wins || 0} buyer engagement(s) in the past.`}>
                      <Trophy className="w-2.5 h-2.5" />
                      seeded from {journey.seeded_scripts_count} winning script{journey.seeded_scripts_count === 1 ? "" : "s"}
                    </span>
                  )}
                  {journey.seeded_debrief_count > 0 && (
                    <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1"
                          data-testid="nurture-debrief-badge"
                          title={`This journey was also informed by ${journey.seeded_debrief_count} promoted Win Debrief(s) — qualitative notes from reps who recently surged.`}>
                      <Trophy className="w-2.5 h-2.5" />
                      + {journey.seeded_debrief_count} recent win{journey.seeded_debrief_count === 1 ? "" : "s"}
                    </span>
                  )}
                </p>
                <p className="text-xs text-violet-600">
                  {journey.total_days}-day plan · {touchpoints.length} touchpoints
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="sm"
                className="border-violet-300 text-violet-700 hover:bg-violet-100"
                onClick={regenerate}
                disabled={regenerating}
                data-testid="nurture-regenerate-btn"
                title="Regenerate the remaining plan using logged feedback"
              >
                <RefreshCw className={`w-3 h-3 mr-1 ${regenerating ? "animate-spin" : ""}`} />
                {regenerating ? "Adapting…" : "Regenerate from feedback"}
              </Button>
              {onDismiss && (
                <Button variant="outline" size="sm"
                        className="border-violet-300 text-violet-700 hover:bg-violet-100"
                        onClick={onDismiss}>Dismiss</Button>
              )}
            </div>
          </div>

          <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
            {touchpoints.map((tp, i) => {
              const status = tp.status || "pending";
              const sm = STATUS_META[status] || STATUS_META.pending;
              const SIcon = sm.icon;
              const ChannelIcon = CHANNEL_ICON[tp.channel] || MessageCircle;
              const isOpen = expandedTp === tp.id;
              return (
                <div
                  key={tp.id || i}
                  className={`bg-white rounded-lg border ${isOpen ? "border-violet-300 shadow-sm" : "border-violet-100"}`}
                  data-testid={`nurture-tp-${tp.id}`}
                >
                  <div className="flex items-start gap-3 p-3 cursor-pointer"
                       onClick={() => setExpandedTp(isOpen ? null : tp.id)}>
                    <div className="w-8 h-8 rounded-full bg-violet-100 border border-violet-200 flex items-center justify-center text-xs font-bold text-violet-700 flex-shrink-0">
                      D{tp.day}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <ChannelIcon className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />
                        <p className="text-sm font-medium text-slate-800 truncate">{tp.action}</p>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border flex items-center gap-1 ${sm.cls}`}>
                          <SIcon className="w-2.5 h-2.5" /> {sm.label}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5 capitalize">{tp.channel?.replace(/_/g, " ")}</p>
                    </div>
                    {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                  </div>

                  {isOpen && (
                    <div className="border-t border-violet-100 p-3 bg-violet-50/30 space-y-3">
                      {tp.talking_points?.length > 0 && (
                        <div>
                          <p className="text-[11px] font-semibold text-violet-700 uppercase tracking-wide mb-1">Talking points</p>
                          <ul className="text-xs text-slate-700 space-y-1 list-disc list-inside">
                            {tp.talking_points.map((p, j) => <li key={j}>{p}</li>)}
                          </ul>
                        </div>
                      )}

                      {tp.script && (
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-[11px] font-semibold text-violet-700 uppercase tracking-wide">Script</p>
                            <Button size="sm" variant="ghost" className="h-6 text-[11px] text-violet-600"
                                    onClick={() => copyScript(tp.script)}
                                    data-testid={`nurture-copy-script-${tp.id}`}>
                              <Copy className="w-3 h-3 mr-1" /> Copy
                            </Button>
                          </div>
                          <pre className="text-xs text-slate-700 bg-white border border-violet-100 rounded p-2 whitespace-pre-wrap font-sans">{tp.script}</pre>
                        </div>
                      )}

                      {tp.objection_reframe && (
                        <div>
                          <p className="text-[11px] font-semibold text-amber-700 uppercase tracking-wide mb-1">If they push back</p>
                          <p className="text-xs text-slate-700">{tp.objection_reframe}</p>
                        </div>
                      )}

                      {tp.success_signal && (
                        <div>
                          <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wide mb-1">Success signal</p>
                          <p className="text-xs text-slate-700">{tp.success_signal}</p>
                        </div>
                      )}

                      {tp.avoid?.length > 0 && (
                        <div>
                          <p className="text-[11px] font-semibold text-red-700 uppercase tracking-wide mb-1">Do NOT say</p>
                          <ul className="text-xs text-slate-700 space-y-1 list-disc list-inside">
                            {tp.avoid.map((a, j) => <li key={j}>{a}</li>)}
                          </ul>
                        </div>
                      )}

                      {tp.feedback?.length > 0 && (
                        <div>
                          <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1">Feedback log</p>
                          <div className="space-y-1">
                            {tp.feedback.map((f, j) => (
                              <div key={j} className="text-[11px] text-slate-600 bg-white border border-slate-100 rounded px-2 py-1">
                                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] mr-2 ${STATUS_META[f.status]?.cls || ""}`}>
                                  {STATUS_META[f.status]?.label || f.status}
                                </span>
                                {f.notes || "—"}
                                <span className="text-slate-400 ml-2">· {f.logged_by_name || "user"} · {new Date(f.logged_at).toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="pt-1 flex items-center gap-2 flex-wrap">
                        <Button size="sm"
                                className="bg-violet-600 text-white hover:bg-violet-700"
                                onClick={(e) => { e.stopPropagation(); openLog(tp); }}
                                data-testid={`nurture-log-btn-${tp.id}`}>
                          <ClipboardList className="w-3 h-3 mr-1" />
                          Log what happened
                        </Button>
                        {(() => {
                          const wins = (tp.feedback || []).filter((f) => f.status === "done").length;
                          const canPromote = tp.script && wins > 0 && PROMOTABLE_CHANNELS.has(tp.channel);
                          if (!canPromote && !tp.promoted_template_id) return null;
                          if (tp.promoted_template_id) {
                            return (
                              <span className="text-[11px] px-2 py-1 rounded-md bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1"
                                    data-testid={`nurture-promoted-badge-${tp.id}`}>
                                <Trophy className="w-3 h-3" /> Promoted to template
                              </span>
                            );
                          }
                          return (
                            <Button size="sm" variant="outline"
                                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                                    onClick={(e) => { e.stopPropagation(); promoteToTemplate(tp); }}
                                    disabled={promotingTp === tp.id}
                                    data-testid={`nurture-promote-btn-${tp.id}`}
                                    title="This script worked — pin it to the Message Template Registry">
                              <Trophy className="w-3 h-3 mr-1" />
                              {promotingTp === tp.id ? "Promoting…" : "Promote to template"}
                            </Button>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {journey.personalization_notes && (
            <div className="mt-3 p-2 bg-white/60 rounded-lg">
              <p className="text-xs text-violet-700"><strong>Notes:</strong> {journey.personalization_notes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Log-feedback dialog */}
      <Dialog open={!!logOpenTp} onOpenChange={(o) => !o && setLogOpenTp(null)}>
        <DialogContent className="sm:max-w-md" data-testid="nurture-log-dialog">
          <DialogHeader>
            <DialogTitle>Log what happened</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-xs text-slate-500">
              Day {logOpenTp?.day} · {logOpenTp?.channel?.replace(/_/g, " ")} · {logOpenTp?.action}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Outcome</label>
              <Select value={logStatus} onValueChange={setLogStatus}>
                <SelectTrigger data-testid="nurture-log-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="done">Done — buyer engaged</SelectItem>
                  <SelectItem value="no_response">No response</SelectItem>
                  <SelectItem value="rescheduled">Rescheduled / deferred</SelectItem>
                  <SelectItem value="negative_response">Negative response</SelectItem>
                  <SelectItem value="skipped">Skipped</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">What actually happened?</label>
              <Textarea
                value={logNotes}
                onChange={(e) => setLogNotes(e.target.value)}
                placeholder="e.g. Buyer said his budget moved to 55L. Wants to see tower C."
                rows={3}
                data-testid="nurture-log-notes"
              />
              <p className="text-[11px] text-slate-400 mt-1">This also posts to the lead timeline as an interaction.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogOpenTp(null)}>Cancel</Button>
            <Button
              onClick={submitLog}
              disabled={logSubmitting}
              className="bg-violet-600 text-white hover:bg-violet-700"
              data-testid="nurture-log-submit"
            >
              {logSubmitting ? "Saving…" : "Log feedback"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NurtureJourneyCard;
